/**
 * GRC Status Badges — visual indicators overlaid on process/BPMN shapes
 *
 * Renders small status indicators showing:
 * - Control status dot (top-right): green/amber/red
 * - Dependency badges (bottom-left): 3P, BOT, MODEL pills
 * - Evidence status (top-right, below control dot): checkmark or circle
 * - Compliance score border tint (shape border color)
 */

import React from 'react';
import { Group, Rect, Circle, Text } from 'react-konva';
import type { Node } from '../../../core/models/scene';
import { useProcessCanvasLayerStore } from '../journey/processCanvasLayerStore';
import type { ProcessOverlayVisibility } from '../../../stores/processViewStore';

interface GrcBadgesProps {
  node: Node;
  visibility?: Pick<
    ProcessOverlayVisibility,
    'complianceBorder' | 'controlStatus' | 'evidenceStatus' | 'dependencies'
  >;
}

function getAttrs(node: Node): Record<string, unknown> {
  return (node as any).attrs ?? (node as any).props?.attrs ?? {};
}

function hasArrayData(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

interface GrcStatus {
  controlDot: 'green' | 'amber' | 'red' | 'none';
  evidenceDot: 'complete' | 'partial' | 'none';
  dependencies: string[]; // ['3P', 'BOT', 'MODEL']
  complianceScore: 'high' | 'medium' | 'low' | 'none';
}

function computeGrcStatus(node: Node): GrcStatus {
  const attrs = getAttrs(node);

  // Control status
  const hasControlPoints = hasArrayData(attrs.controlPoints);
  const hasControls = hasArrayData(attrs.controls);
  let controlDot: GrcStatus['controlDot'] = 'none';
  if (hasControlPoints) controlDot = 'green';
  else if (hasControls) controlDot = 'amber';
  else controlDot = 'none';

  // Evidence status
  const hasEvidence = hasArrayData(attrs.evidence);
  const hasFindings = hasArrayData(attrs.findings);
  let evidenceDot: GrcStatus['evidenceDot'] = 'none';
  if (hasEvidence && !hasFindings) evidenceDot = 'complete';
  else if (hasEvidence) evidenceDot = 'partial';

  // Dependencies
  const deps: string[] = [];
  if (hasArrayData(attrs.thirdPartyDependencies)) deps.push('3P');
  if (hasArrayData(attrs.botDependencies)) deps.push('BOT');
  if (hasArrayData(attrs.modelDependencies)) deps.push('MDL');

  // Compliance score (composite of controls + evidence + control points)
  let complianceScore: GrcStatus['complianceScore'] = 'none';
  if (hasControls || hasControlPoints || hasEvidence) {
    const score = (hasControlPoints ? 1 : 0) + (hasEvidence ? 1 : 0) + (hasControls ? 1 : 0);
    if (score >= 3) complianceScore = 'high';
    else if (score >= 2) complianceScore = 'medium';
    else complianceScore = 'low';
  }

  return { controlDot, evidenceDot, dependencies: deps, complianceScore };
}

const CONTROL_DOT_COLORS: Record<string, string> = {
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
};

const COMPLIANCE_BORDER_COLORS: Record<string, string> = {
  high: '#22c55e',
  medium: '#f59e0b',
  low: '#ef4444',
};

const DEP_COLORS: Record<string, string> = {
  '3P': '#6366f1',
  'BOT': '#0ea5e9',
  'MDL': '#8b5cf6',
};

const ALLOWED_BADGE_TYPES = new Set<string>([
  'flowchart-shape',
  'bpmn-shape',
  'process',
  'decision',
]);

function isAssurableType(type: string | undefined): boolean {
  if (!type) return false;
  if (ALLOWED_BADGE_TYPES.has(type)) return true;
  // Network domain resources go through the shape-manifest path with
  // type names like `aws_ec2`, `aws_s3`, `basic_router`, etc. They can
  // carry the same controls / evidence / findings props as process
  // shapes once federated assurance data is attached, so we allow the
  // provider-neutral prefixes through here.
  if (type.startsWith('aws_')) return true;
  if (type.startsWith('basic_')) return true;
  if (type.startsWith('azure_')) return true;
  if (type.startsWith('gcp_')) return true;
  return false;
}

export const GrcBadges: React.FC<GrcBadgesProps> = ({ node, visibility }) => {
  // Compliance-layer gate: in a process folder that hasn't opted into
  // the Compliance track, the GRC overlay (border + dots) is hidden.
  // Default-true store leaves network/cloud + compliance-on folders
  // unaffected. See docs/product/process-user-journey.md §6.
  const complianceActive = useProcessCanvasLayerStore((s) => s.complianceActive);
  const attrs = getAttrs(node);

  // Skip pool tables and non-assurable nodes
  if (!complianceActive) return null;
  if (attrs.__isPoolTable) return null;
  const type = (node as any).type as string | undefined;
  if (!isAssurableType(type)) return null;

  const status = computeGrcStatus(node);
  const showComplianceBorder = visibility?.complianceBorder ?? true;
  const showControlStatus = visibility?.controlStatus ?? true;
  const showEvidenceStatus = visibility?.evidenceStatus ?? true;
  const showDependencies = visibility?.dependencies ?? true;

  // Skip if no GRC data at all
  if (
    (!showControlStatus || status.controlDot === 'none') &&
    (!showEvidenceStatus || status.evidenceDot === 'none') &&
    (!showDependencies || status.dependencies.length === 0) &&
    (!showComplianceBorder || status.complianceScore === 'none')
  ) {
    return null;
  }

  const x = (node.x ?? 0) as number;
  const y = (node.y ?? 0) as number;
  const w = ((node as any).w ?? (node as any).width ?? 120) as number;
  const h = ((node as any).h ?? (node as any).height ?? 80) as number;

  return (
    <Group listening={false}>
      {/* Compliance score — colored border highlight */}
      {showComplianceBorder && status.complianceScore !== 'none' && (
        <Rect
          x={x - 2}
          y={y - 2}
          width={w + 4}
          height={h + 4}
          stroke={COMPLIANCE_BORDER_COLORS[status.complianceScore]}
          strokeWidth={2.5}
          cornerRadius={6}
          listening={false}
        />
      )}

      {/* Control status dot — top right corner */}
      {showControlStatus && status.controlDot !== 'none' && (
        <>
          <Circle
            x={x + w - 2}
            y={y + 2}
            radius={5}
            fill={CONTROL_DOT_COLORS[status.controlDot]}
            stroke="#ffffff"
            strokeWidth={1.5}
            listening={false}
          />
        </>
      )}

      {/* Evidence status dot — below control dot */}
      {showEvidenceStatus && status.evidenceDot !== 'none' && (
        <Group x={x + w - 2} y={y + (showControlStatus && status.controlDot !== 'none' ? 16 : 2)}>
          <Circle
            radius={5}
            fill={status.evidenceDot === 'complete' ? '#22c55e' : '#f59e0b'}
            stroke="#ffffff"
            strokeWidth={1.5}
            listening={false}
          />
          {status.evidenceDot === 'complete' && (
            <Text
              x={-3}
              y={-4.5}
              text="✓"
              fontSize={7}
              fill="#ffffff"
              fontStyle="bold"
              listening={false}
            />
          )}
        </Group>
      )}

      {/* Dependency badges — bottom left */}
      {showDependencies && status.dependencies.length > 0 && (
        <Group x={x} y={y + h + 4}>
          {status.dependencies.map((dep, i) => {
            const badgeW = dep.length * 6 + 8;
            const offsetX = i * (badgeW + 3);
            return (
              <Group key={dep} x={offsetX} y={0}>
                <Rect
                  width={badgeW}
                  height={14}
                  fill={DEP_COLORS[dep] ?? '#64748b'}
                  cornerRadius={3}
                  listening={false}
                />
                <Text
                  x={0}
                  y={2}
                  width={badgeW}
                  height={14}
                  text={dep}
                  fontSize={8}
                  fontStyle="bold"
                  fill="#ffffff"
                  align="center"
                  listening={false}
                />
              </Group>
            );
          })}
        </Group>
      )}
    </Group>
  );
};
